import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider
} from '@mui/material';
import authApi from '../../services/authApi';
import { useUserSession } from '../../contexts/UserContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha } from '@mui/material/styles';
import { useSharePoint } from '../../contexts/SharePointContext';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const StorageGrowthReport = () => {
  const { userSession } = useUserSession();
  const { selectedConfig, refreshTrigger } = useSharePoint();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storageData, setStorageData] = useState({ libraries: [], total: [] });
  const [selectedLibraries, setSelectedLibraries] = useState(new Set());
  const [showTotal, setShowTotal] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState([0, 100]); // Index values for the slider
  const [availableDates, setAvailableDates] = useState([]);

  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
    // Generate more colors by adjusting hue
    ...Array.from({ length: 10 }, (_, i) => 
      `hsl(${(i * 30) % 360}, 70%, 50%)`
    )
  ];

  const fetchStorageData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authApi.getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/datawarehouse/storage-growth`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            configId: selectedConfig
          }
        }
      );
      setStorageData(response.data);
      // Initially select all libraries
      setSelectedLibraries(new Set(response.data.libraries.map(lib => lib.library_name)));
    } catch (error) {
      console.error('Error fetching storage data:', error);
      setError('Failed to fetch storage growth data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userSession?.email || !selectedConfig) {
      setLoading(false);
      setStorageData({ libraries: [], total: [] });
      setSelectedLibraries(new Set());
      return;
    }
    fetchStorageData();
  }, [userSession?.email, userSession?.impersonatedConfigId, selectedConfig, refreshTrigger]);

  useEffect(() => {
    if (storageData.total.length > 0) {
      // Get all unique dates and sort them
      const dates = storageData.total.map(item => new Date(item.event_month))
        .sort((a, b) => a - b);
      setAvailableDates(dates);

      // Set initial range to full available dates
      const maxIndex = dates.length - 1;
      setDateRange([0, maxIndex]);
    }
  }, [storageData.total]);

  const getFilteredData = (data) => {
    if (!data || !data.length || !availableDates.length) return [];
    
    const startDate = availableDates[dateRange[0]];
    const endDate = availableDates[dateRange[1]];
    
    return data.filter(item => {
      const itemDate = new Date(item.event_month);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const formatSliderLabel = (value) => {
    if (!availableDates.length) return '';
    const date = availableDates[Math.round(value)];
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handleDateRangeChange = (event, newValue) => {
    setDateRange(newValue);
  };

  const handleLibraryToggle = (libraryName) => {
    const newSelected = new Set(selectedLibraries);
    if (newSelected.has(libraryName)) {
      newSelected.delete(libraryName);
    } else {
      newSelected.add(libraryName);
    }
    setSelectedLibraries(newSelected);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLibraries(new Set(storageData.libraries.map(lib => lib.library_name)));
    } else {
      setSelectedLibraries(new Set());
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined || isNaN(bytes)) {
      return '0.00 GB';
    }
    const gb = Number(bytes);
    return `${gb.toFixed(2)} GB`;
  };

  // Calculate total for selected libraries
  const calculateSelectedTotal = (month) => {
    return storageData.libraries
      .filter(lib => selectedLibraries.has(lib.library_name))
      .reduce((total, lib) => {
        const monthData = lib.data.find(d => 
          new Date(d.event_month).getTime() === new Date(month).getTime()
        );
        return total + (monthData ? Number(monthData.cumulative_size) : 0);
      }, 0);
  };

  const chartData = {
    labels: getFilteredData(storageData.total).map(item => 
      new Date(item.event_month).toLocaleDateString('en-US', { 
        month: 'short', 
        year: 'numeric' 
      })
    ),
    datasets: [
      // Show selected libraries
      ...storageData.libraries
        .filter(lib => selectedLibraries.has(lib.library_name))
        .map((lib, index) => ({
          label: lib.library_name,
          data: getFilteredData(lib.data).map(item => Number(item.cumulative_size)),
          borderColor: colors[index % colors.length],
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 1,
          pointHoverRadius: 5,
          tension: 0.4
        })),
      // Show total if enabled
      ...(showTotal ? [{
        label: 'Selected Total',
        data: getFilteredData(storageData.total).map(item => Number(calculateSelectedTotal(item.event_month))),
        borderColor: theme.palette.grey[800],
        backgroundColor: alpha(theme.palette.grey[800], 0.1),
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0
      }] : [])
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 6
        }
      },
      title: {
        display: true,
        text: 'Storage Growth Over Time (GB)'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${formatBytes(context.raw)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatBytes(value)
        },
        grid: {
          color: theme.palette.grey[200]
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={'storage-growth'}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Storage Growth Analysis
            </Typography>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchStorageData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Date Range Slider */}
          <Box sx={{ px: 3, py: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Date Range
            </Typography>
            <Slider
              value={dateRange}
              onChange={handleDateRangeChange}
              valueLabelDisplay="auto"
              valueLabelFormat={formatSliderLabel}
              min={0}
              max={availableDates.length - 1}
              step={1}
              marks={[
                { value: 0, label: formatSliderLabel(0) },
                { value: availableDates.length - 1, label: formatSliderLabel(availableDates.length - 1) }
              ]}
              sx={{ mt: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Selected Range: {formatSliderLabel(dateRange[0])} - {formatSliderLabel(dateRange[1])}
              </Typography>
            </Box>
          </Box>

          <Accordion 
            expanded={expanded} 
            onChange={() => setExpanded(!expanded)}
            sx={{ 
              mb: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }
              }}
            >
              <Typography variant="h6">Library Selection</Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedLibraries.size} of {storageData.libraries.length} selected
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedLibraries.size === storageData.libraries.length}
                      indeterminate={selectedLibraries.size > 0 && selectedLibraries.size < storageData.libraries.length}
                      onChange={handleSelectAll}
                    />
                  }
                  label="Select All Libraries"
                />
                <Divider sx={{ my: 1 }} />
                {storageData.libraries.map((lib) => (
                  <FormControlLabel
                    key={lib.container_id}
                    control={
                      <Checkbox
                        checked={selectedLibraries.has(lib.library_name)}
                        onChange={() => handleLibraryToggle(lib.library_name)}
                      />
                    }
                    label={lib.library_name}
                  />
                ))}
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showTotal}
                      onChange={(e) => setShowTotal(e.target.checked)}
                    />
                  }
                  label="Show Total Line"
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: '500px' }}>
              {!userSession?.email || !selectedConfig ? (
                <Alert severity="info">
                  Select a SharePoint configuration to load storage growth data.
                </Alert>
              ) : storageData.libraries.length > 0 ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <Alert severity="info">
                  No storage data available.
                </Alert>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StorageGrowthReport; 